import { ShieldCheck, Archive, FileText, Clock, AlertTriangle } from "lucide-react";
import { PageHeader, Card, Table, Td, Badge } from "../../components/UI";

const RULES = [
  { area: "HIPAA baseline", rule: "45 CFR § 164.316(b)(2)(i) - policies, procedures, and PHI-related documentation retained for 6 years from creation or last effective date.", scope: "Federal minimum" },
  { area: "Cocoa EHR - Adults", rule: "7 years post last-service for every clinical record.", scope: "Default policy" },
  { area: "Cocoa EHR - Minors", rule: "7 years post last-service, or until the client turns 21, whichever is later.", scope: "Default policy" },
  { area: "Behavioral health notes", rule: "Retained per adult or minor rule above; may not be destroyed while any legal or regulatory hold is active.", scope: "Default policy" },
  { area: "Suicide risk screens", rule: "Retained for the full lifetime of the client record, never separately archived.", scope: "Joint Commission NPSG 15.01.01" },
  { area: "Audit trail", rule: "All create, update, archive, and access events are written to the audit log with actor, timestamp, and reason; audit log is retained indefinitely.", scope: "HIPAA + best practice" },
];

const BEHAVIOR_RULES = [
  { action: "Delete a clinical record", allowed: false, note: "Records are always soft-deleted (archived). The row is hidden from default lists but preserved in storage." },
  { action: "Archive a clinical record", allowed: true, note: "Admin, clinician, or front-desk with edit-clinical permission can archive with an optional reason. Archived rows are viewable via the 'Show archived' filter." },
  { action: "Edit a signed record", allowed: false, note: "Signed and locked records are immutable. Use the Addendum flow to append changes." },
  { action: "Add an addendum", allowed: true, note: "Reason, text, and e-signature required. Addenda are timestamped and appended; original content is preserved." },
  { action: "Permanently destroy PHI", allowed: false, note: "Not supported. Would violate HIPAA § 164.316(b)(2)(i)." },
];

export default function DataRetention() {
  return (
    <div data-testid="data-retention-page">
      <PageHeader title="Data Retention and HIPAA Policy"
        subtitle="How Cocoa EHR retains PHI, archives records, and preserves the audit trail" />

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <Card className="p-4" data-testid="dr-card-adults">
          <div className="flex items-center gap-2 text-[#7B6157]"><Clock size={14} /><span className="text-[10px] font-bold uppercase tracking-wide">Adult records</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">7 years</div>
          <div className="text-xs text-[#7B6157]">post last-service</div>
        </Card>
        <Card className="p-4" data-testid="dr-card-minors">
          <div className="flex items-center gap-2 text-[#7B6157]"><Clock size={14} /><span className="text-[10px] font-bold uppercase tracking-wide">Minors</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">7 yrs or age 21</div>
          <div className="text-xs text-[#7B6157]">whichever is later</div>
        </Card>
        <Card className="p-4" data-testid="dr-card-federal">
          <div className="flex items-center gap-2 text-[#7B6157]"><ShieldCheck size={14} /><span className="text-[10px] font-bold uppercase tracking-wide">Federal minimum</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">6 years</div>
          <div className="text-xs text-[#7B6157]">HIPAA § 164.316(b)(2)(i)</div>
        </Card>
      </div>

      <Card className="mb-4">
        <div className="flex items-center gap-2 border-b border-[#F1EEE7] px-4 py-2 text-sm font-bold text-[#3E2723]">
          <FileText size={14} className="text-[#7B6157]" /> Retention rules
        </div>
        <Table headers={["Area", "Rule", "Scope"]}>
          {RULES.map((r, i) => (
            <tr key={i} data-testid={`dr-rule-${i}`}>
              <Td className="font-semibold">{r.area}</Td>
              <Td className="text-[#5D4037]">{r.rule}</Td>
              <Td><Badge value={r.scope} /></Td>
            </tr>
          ))}
        </Table>
      </Card>

      <Card>
        <div className="flex items-center gap-2 border-b border-[#F1EEE7] px-4 py-2 text-sm font-bold text-[#3E2723]">
          <Archive size={14} className="text-[#7B6157]" /> What every user can and cannot do
        </div>
        <Table headers={["Action", "Allowed?", "Details"]}>
          {BEHAVIOR_RULES.map((r, i) => (
            <tr key={i} data-testid={`dr-behavior-${i}`}>
              <Td className="font-semibold">{r.action}</Td>
              <Td>
                {r.allowed ? (
                  <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]">Allowed</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">
                    <AlertTriangle size={9} /> Not allowed
                  </span>
                )}
              </Td>
              <Td className="text-[#5D4037]">{r.note}</Td>
            </tr>
          ))}
        </Table>
      </Card>

      <div className="mt-4 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-xs text-[#6D4C41]" data-testid="dr-footer">
        <div className="font-bold text-[#3E2723]">A note on state variations</div>
        <div className="mt-1">Some states require longer retention (California adults 7 years, minors until 25; Texas adults 7 years, minors until 21; Florida 5 years). When a stricter state or payer rule applies, Cocoa EHR takes the maximum of federal, state, and payer rules automatically. Contact your Privacy Officer if a legal hold requires extended retention beyond default.</div>
      </div>
    </div>
  );
}
